import { lazy, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Toast, Select } from "@/shared/ui";
import { useGetProjectsQuery } from "@/features/projects";
import { useTaxPage } from "../../hooks";
import { buildFno100, buildFno200, downloadJson, formatDeclarationDate } from "../../utils";
import { MAIN_TAB_ICONS } from "../../consts";
import { TaxHeader, TaxParametersForm, DeclarationHeader, TaxTabs, TaxFormSkeleton, TaxMainTabs } from "./components";

const TaxContent = lazy(() => import("./components/TaxContent").then((m) => ({ default: m.default })));


export default function TaxPage() {
  const { t } = useTranslation("TaxPage");

  const {
    taxType,
    setTaxType,
    year,
    setYear,
    quarter,
    setQuarter,
    endDate,
    setEndDate,
    includeGph,
    setIncludeGph,
    activeForm,
    setActiveForm,
    isReportGenerated,
    generateReport,
    form100Data,
    form100Loading,
    form100Error,
    form10001Data,
    form10001Loading,
    form10001Error,
    form10002Data,
    form10002Loading,
    form10002Error,
    form10007Data,
    form10007Loading,
    form10007Error,
    form10010Data,
    form10010Loading,
    form10010Error,
    form10011Data,
    form10011Loading,
    form10011Error,
    projectId,
    setProjectId,
    form200Data,
    form200Loading,
    form200Error,
    form20001Data,
    form20001Loading,
    form20001Error,
    form20005Data,
    form20005Loading,
    form20005Error,
    taxpayerMeta,
    form10007ExportData,
    locale,
  } = useTaxPage();

  const mainTabItems = [
    { id: "cit", label: t("tabs.main.cit"), icon: MAIN_TAB_ICONS.cit },
    { id: "payroll", label: t("tabs.main.payroll"), icon: MAIN_TAB_ICONS.payroll },
  ];

  const { data: projects } = useGetProjectsQuery(undefined, {
    skip: (activeForm !== "100.10" && activeForm !== "100.11") || !isReportGenerated.cit,
  });

  const projectOptions = useMemo(() => {
    if (!projects) return [];
    return projects.map((p) => ({ label: p.name, value: p.id }));
  }, [projects]);

  const isReportGeneratedForCurrentType = isReportGenerated[taxType];

  function handleGenerate() {
    generateReport(taxType);
  }

  function handleDownloadIsna() {
    const declarationDate = formatDeclarationDate(new Date());
    if (taxType === "cit") {
      if (!form100Data || !form10001Data || !form10002Data || !taxpayerMeta || !year) return;
      const json = buildFno100({
        form100: form100Data,
        form10001: form10001Data,
        form10002: form10002Data,
        form10007: form10007ExportData,
        meta: taxpayerMeta,
        year,
        declarationDate,
      });
      downloadJson(json, `fno100_${year}.json`);
    } else {
      if (!form200Data || !form20001Data || !taxpayerMeta || !year || !quarter) return;
      const json = buildFno200({
        form200: form200Data,
        form20001: form20001Data,
        meta: taxpayerMeta,
        year,
        quarter,
        declarationDate,
      });
      downloadJson(json, `fno200_${year}_q${quarter}.json`);
    }
  }

  const canDownloadIsna =
    taxType === "cit"
      ? activeForm !== "100.10" && activeForm !== "100.11" && !!form100Data && !!form10001Data && !!form10002Data && !!taxpayerMeta
      : !!form200Data && !!form20001Data && !!taxpayerMeta;

  const isLoadingAnyForm =
    taxType === "cit"
      ? form100Loading || form10001Loading || form10002Loading
      : form200Loading || form20001Loading || form20005Loading;

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
          <TaxHeader />

          <div className="mb-6">
            <TaxMainTabs items={mainTabItems} activeId={taxType} onChange={(id) => setTaxType(id as typeof taxType)} />
          </div>

          {!isReportGeneratedForCurrentType ? (
            <TaxParametersForm
              taxType={taxType}
              year={year}
              onYearChange={setYear}
              quarter={quarter}
              onQuarterChange={setQuarter}
              endDate={endDate}
              onEndDateChange={setEndDate}
              includeGph={includeGph}
              onIncludeGphChange={setIncludeGph}
              locale={locale}
              onGenerate={handleGenerate}
            />
          ) : (
            <>
              {isLoadingAnyForm ? (
                <TaxFormSkeleton />
              ) : (
                <div className="radius-3xl surface-base-fill p-7">
                  <DeclarationHeader
                    taxType={taxType}
                    activeForm={activeForm}
                    year={year}
                    quarter={quarter}
                    onDownloadIsna={canDownloadIsna ? handleDownloadIsna : undefined}
                  />

                  <TaxTabs taxType={taxType} activeForm={activeForm} onFormChange={setActiveForm} />

                  {(activeForm === "100.10" || activeForm === "100.11") && (
                    <div className="flex items-center gap-2 mt-4">
                      <Select<number>
                        options={projectOptions}
                        value={projectId}
                        onChange={setProjectId}
                        placeholder={t("filters.projectPlaceholder")}
                        clearable
                      />
                    </div>
                  )}

                  <div className="my-4">
                    <Toast color="grey" text={t("toast.info")} closable={false} autoClose={false} isFullWidth />
                  </div>

                  <Suspense fallback={<TaxFormSkeleton />}>
                    <TaxContent
                      taxType={taxType}
                      activeForm={activeForm}
                      form100Data={form100Data}
                      form100Loading={form100Loading}
                      form100Error={form100Error}
                      form10001Data={form10001Data}
                      form10001Loading={form10001Loading}
                      form10001Error={form10001Error}
                      form10002Data={form10002Data}
                      form10002Loading={form10002Loading}
                      form10002Error={form10002Error}
                      form10007Data={form10007Data}
                      form10007Loading={form10007Loading}
                      form10007Error={form10007Error}
                      form10010Data={form10010Data}
                      form10010Loading={form10010Loading}
                      form10010Error={form10010Error}
                      form10011Data={form10011Data}
                      form10011Loading={form10011Loading}
                      form10011Error={form10011Error}
                      form200Data={form200Data}
                      form200Loading={form200Loading}
                      form200Error={form200Error}
                      form20001Data={form20001Data}
                      form20001Loading={form20001Loading}
                      form20001Error={form20001Error}
                      form20005Data={form20005Data}
                      form20005Loading={form20005Loading}
                      form20005Error={form20005Error}
                    />
                  </Suspense>
                </div>
              )}
            </>
          )}
      </section>
    </>
  );
}
